import {
  Badge,
  Button,
  Card,
  CardContent,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Input,
  Label,
  Switch,
} from "@devhop/ui";
import { RiArrowDownSLine, RiArrowUpSLine } from "@remixicon/react";
import { Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import {
  usePatchSessionPromo,
  useSessionPromoQuery,
} from "@/modules/cf-feed/presentation/api/queries";
import {
  formatKip,
  formatPriceInput,
  maskDiscountFixedLakInput,
  maskPriceInput,
  parseDiscountFixedLakInput,
  parsePriceInput,
} from "@/modules/cf-feed/presentation/ui/table-helpers";

type SessionPromoPanelProps = {
  feedId: string;
};

type PrintDraft = {
  code: string;
  adminOptionDefinitionId: string;
  label: string;
  posDefaultSurchargeLak: number;
  enabled: boolean;
  surchargeLak: string;
  isAutoAttach: boolean;
  requiresCustomerInput: boolean;
};

type PromoTag = {
  key: string;
  label: string;
};

function parseOptionalPercent(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const n = Number.parseFloat(t);
  if (!Number.isFinite(n)) return null;
  return n;
}

function parseOptionalLak(raw: string): number | null {
  if (!raw.trim()) return null;
  return parsePriceInput(raw);
}

function parseOptionalDiscountFixedLak(raw: string): number | null {
  if (!raw.trim()) return null;
  return parseDiscountFixedLakInput(raw);
}

export function SessionPromoPanel({ feedId }: SessionPromoPanelProps) {
  const query = useSessionPromoQuery(feedId);
  const patch = usePatchSessionPromo(feedId);

  const [open, setOpen] = useState(false);
  const [listPercent, setListPercent] = useState("");
  const [listFixed, setListFixed] = useState("");
  const [freeShipping, setFreeShipping] = useState(false);
  const [shippingFee, setShippingFee] = useState("");
  const [printDrafts, setPrintDrafts] = useState<PrintDraft[]>([]);

  useEffect(() => {
    const promo = query.data?.promo;
    if (!promo) return;
    setListPercent(
      promo.listDiscountPercent == null
        ? ""
        : String(promo.listDiscountPercent),
    );
    setListFixed(
      promo.listDiscountFixedLak == null
        ? ""
        : formatPriceInput(promo.listDiscountFixedLak),
    );
    setFreeShipping(promo.freeShipping);
    setShippingFee(
      promo.shippingFeeLak == null
        ? ""
        : formatPriceInput(promo.shippingFeeLak),
    );
    setPrintDrafts(
      (promo.printOptions ?? []).map((opt) => ({
        code: opt.code,
        adminOptionDefinitionId: opt.adminOptionDefinitionId,
        label: opt.label,
        posDefaultSurchargeLak: opt.posDefaultSurchargeLak,
        enabled: opt.enabled,
        surchargeLak: formatPriceInput(opt.surchargeLak),
        isAutoAttach: opt.isAutoAttach,
        requiresCustomerInput: opt.requiresCustomerInput,
      })),
    );
  }, [query.data?.promo]);

  const openTags = useMemo((): PromoTag[] => {
    const tags: PromoTag[] = [];
    const percent = parseOptionalPercent(listPercent);
    const fixed = parseOptionalDiscountFixedLak(listFixed);
    if (percent != null && percent > 0) {
      tags.push({ key: "list_pct", label: `ສ່ວນຫຼຸດລາຍການ ${percent}%` });
    }
    if (fixed != null && fixed > 0) {
      tags.push({
        key: "list_fixed",
        label: `ສ່ວນຫຼຸດລາຍການ -${formatPriceInput(fixed)}`,
      });
    }
    if (freeShipping) {
      tags.push({ key: "free_shipping", label: "ສົ່ງຟຣີ" });
    }
    for (const d of printDrafts) {
      if (!d.enabled) continue;
      const price = parsePriceInput(d.surchargeLak) ?? 0;
      tags.push({
        key: d.code,
        label:
          price === 0
            ? `${d.label} ຟຣີ`
            : `${d.label} +${formatPriceInput(price)}`,
      });
    }
    return tags;
  }, [listPercent, listFixed, freeShipping, printDrafts]);

  const handleSave = () => {
    patch.mutate({
      listDiscountPercent: parseOptionalPercent(listPercent),
      listDiscountFixedLak: parseOptionalDiscountFixedLak(listFixed),
      freeShipping,
      shippingFeeLak: freeShipping ? 0 : parseOptionalLak(shippingFee),
      printOptions: printDrafts.map((d) => ({
        code: d.code,
        enabled: d.enabled,
        surchargeLak: parsePriceInput(d.surchargeLak) ?? 0,
        isAutoAttach: d.isAutoAttach,
        requiresCustomerInput: d.requiresCustomerInput,
      })),
    });
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <Collapsible open={open} onOpenChange={setOpen}>
          <div className="space-y-3">
            <CollapsibleTrigger asChild>
              <button
                type="button"
                className="flex w-full items-start justify-between gap-3 text-left"
              >
                <div className="min-w-0 space-y-1">
                  <h2 className="font-semibold text-base">ໂປຣໂພສ / Live</h2>
                  <p className="text-muted-foreground text-sm">
                    ສົ່ງຟຣີ, ສ່ວນຫຼຸດທັງລາຍການ, ຊື່ / ໝາຍເລກ (override ໂພສນີ້)
                  </p>
                </div>
                <span className="inline-flex shrink-0 items-center gap-1 rounded-md border border-border px-2 py-1 text-muted-foreground text-xs">
                  {open ? "ປິດ" : "ແກ້ໄຂ"}
                  {open ? (
                    <RiArrowUpSLine className="size-4" aria-hidden />
                  ) : (
                    <RiArrowDownSLine className="size-4" aria-hidden />
                  )}
                </span>
              </button>
            </CollapsibleTrigger>

            <div className="flex flex-wrap gap-2">
              {openTags.length > 0 ? (
                openTags.map((tag) => (
                  <Badge
                    key={tag.key}
                    variant="secondary"
                    className="font-normal tabular-nums"
                  >
                    {tag.label}
                  </Badge>
                ))
              ) : (
                <Badge
                  variant="outline"
                  className="font-normal text-muted-foreground"
                >
                  ຍັງບໍ່ເປີດໂປຣ
                </Badge>
              )}
            </div>
          </div>

          <CollapsibleContent className="mt-4 space-y-5">
            <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-muted-foreground text-xs">
              ຄ່າພິມເລີ່ມຕົ້ນລະບົບ (NAME_PRINT / NUMBER_PRINT) ຕັ້ງທີ່{" "}
              <Link
                to="/app/print-fee-settings"
                className="font-medium text-foreground underline underline-offset-2"
              >
                ຄ່າພິມຊື່ / ໝາຍເລກ
              </Link>
              {" — "}ຂ້າງລຸ່ມນີ້ແມ່ນ override ສະເພາະໂພສນີ້ເທົ່ານັ້ນ.
            </p>

            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-1.5">
                <Label htmlFor="cf-list-discount-percent">
                  ສ່ວນຫຼຸດທັງລາຍການ (%)
                </Label>
                <Input
                  id="cf-list-discount-percent"
                  inputMode="decimal"
                  placeholder="ເຊັ່ນ 10"
                  value={listPercent}
                  onChange={(e) => setListPercent(e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf-list-discount-fixed">
                  ສ່ວນຫຼຸດທັງລາຍການ (LAK / ຊິ້ນ)
                </Label>
                <Input
                  id="cf-list-discount-fixed"
                  inputMode="numeric"
                  className="tabular-nums"
                  placeholder="5.000"
                  value={listFixed}
                  onChange={(e) => setListFixed(maskPriceInput(e.target.value))}
                  onBlur={() =>
                    setListFixed(maskDiscountFixedLakInput(listFixed))
                  }
                />
                <p className="text-muted-foreground text-xs">
                  ປັດເປັນຫຼັກພັນ (x,000)
                </p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="cf-shipping-fee">ຄ່າສົ່ງ (LAK)</Label>
                <Input
                  id="cf-shipping-fee"
                  inputMode="numeric"
                  className="tabular-nums"
                  disabled={freeShipping}
                  placeholder="0"
                  value={freeShipping ? "0" : shippingFee}
                  onChange={(e) =>
                    setShippingFee(maskPriceInput(e.target.value))
                  }
                />
              </div>
            </div>
            <p className="text-muted-foreground text-xs">
              ສ່ວນຫຼຸດລາຍການໃຊ້ກັບທຸກສິນຄ້າທີ່ບໍ່ມີສ່ວນຫຼຸດລາຍຕົວ (% ກ່ອນ ແລ້ວຫັກ LAK; ບໍ່ລົດ option)
            </p>

            <div className="space-y-3">
              <div>
                <h3 className="font-medium text-sm">
                  Option ຊື່ / ໝາຍເລກ (override ໂພສ)
                </h3>
                <p className="text-muted-foreground text-xs">
                  ເປີດແລ້ວຜູກກັບທຸກສິນຄ້າໃນໂພສ — ວ່າງລາຄາ = ໃຊ້ຄ່າເລີ່ມຕົ້ນລະບົບ; 0 = ຟຣີ
                </p>
              </div>
              {printDrafts.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  ຍັງບໍ່ພົບ NAME_PRINT / NUMBER_PRINT ໃນ POS — ຮັນ{" "}
                  <code className="text-xs">
                    bun run seed:cf-session-pricing
                  </code>
                </p>
              ) : (
                <ul className="space-y-3">
                  {printDrafts.map((d) => (
                    <li
                      key={d.code}
                      className="rounded-md border border-border p-3"
                    >
                      <div className="mb-2 flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm">{d.label}</p>
                          <p className="text-muted-foreground text-xs">
                            ຄ່າເລີ່ມຕົ້ນລະບົບ: {formatKip(d.posDefaultSurchargeLak)}
                          </p>
                        </div>
                        <Switch
                          checked={d.enabled}
                          onCheckedChange={(checked) =>
                            setPrintDrafts((prev) =>
                              prev.map((row) =>
                                row.code === d.code
                                  ? { ...row, enabled: checked }
                                  : row,
                              ),
                            )
                          }
                        />
                      </div>
                      {d.enabled ? (
                        <div className="grid gap-2 sm:grid-cols-2">
                          <div>
                            <Label
                              htmlFor={`promo-price-${d.code}`}
                              className="mb-1 block text-xs"
                            >
                              ລາຄາ CF ໂພສນີ້ (0 = ຟຣີ)
                            </Label>
                            <Input
                              id={`promo-price-${d.code}`}
                              inputMode="numeric"
                              className="tabular-nums"
                              value={d.surchargeLak}
                              onChange={(e) =>
                                setPrintDrafts((prev) =>
                                  prev.map((row) =>
                                    row.code === d.code
                                      ? {
                                          ...row,
                                          surchargeLak: maskPriceInput(
                                            e.target.value,
                                          ),
                                        }
                                      : row,
                                  ),
                                )
                              }
                            />
                          </div>
                          <div className="flex flex-col justify-end gap-2">
                            <Label
                              htmlFor={`promo-auto-${d.code}`}
                              className="flex items-center gap-2 text-xs"
                            >
                              <Switch
                                id={`promo-auto-${d.code}`}
                                checked={d.isAutoAttach}
                                onCheckedChange={(checked) =>
                                  setPrintDrafts((prev) =>
                                    prev.map((row) =>
                                      row.code === d.code
                                        ? { ...row, isAutoAttach: checked }
                                        : row,
                                    ),
                                  )
                                }
                              />
                              ຕິດອໍເດີອັດຕະໂນມັດ
                            </Label>
                            <Label
                              htmlFor={`promo-input-${d.code}`}
                              className="flex items-center gap-2 text-xs"
                            >
                              <Switch
                                id={`promo-input-${d.code}`}
                                checked={d.requiresCustomerInput}
                                onCheckedChange={(checked) =>
                                  setPrintDrafts((prev) =>
                                    prev.map((row) =>
                                      row.code === d.code
                                        ? {
                                            ...row,
                                            requiresCustomerInput: checked,
                                          }
                                        : row,
                                    ),
                                  )
                                }
                              />
                              ຕ້ອງໃຫ້ລູກຄ້າກອກ
                            </Label>
                          </div>
                        </div>
                      ) : null}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="flex flex-wrap items-center justify-between gap-3">
              <Label
                htmlFor="session-promo-free-shipping"
                className="flex items-center gap-2 text-sm"
              >
                <Switch
                  id="session-promo-free-shipping"
                  checked={freeShipping}
                  onCheckedChange={setFreeShipping}
                />
                ສົ່ງຟຣີທັງໂພສ
              </Label>
              <Button
                type="button"
                onClick={handleSave}
                disabled={patch.isPending || query.isLoading}
              >
                {patch.isPending ? "ກຳລັງບັນທຶກ..." : "ບັນທຶກໂປຣໂພສ"}
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>
      </CardContent>
    </Card>
  );
}
